import { isTicketOverdue } from "../utils/ticketDueDate";
import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ticketsApi, companiesApi, techniciansApi, contactsApi } from "../services/api";
import CompanySelect from "../components/CompanySelect";
import EmployeeAvatar from "../components/EmployeeAvatar";
import TicketCreateForm, { EMPTY_TICKET_FORM } from "../components/TicketCreateForm";
import { degradedAnalysisLabel } from "../components/degradedAnalysisVocabulary";
import { ticketCategoryLabel } from "../components/ticketCategoryVocabulary";
import { TICKET_STATUS_HE, ticketStatusSelectOptions } from "../components/ticketStatusVocabulary";
import { useFilterMine, useTechnicianLookup } from "../context/FilterMineContext";
import { useNotification } from "../context/NotificationContext";
import { attachmentCount, ticketHasAttachments } from "../utils/attachments";
import { confirmTicketDeletion } from "../utils/ticketDeletion";
import { jerusalemDayBound, jerusalemPeriodStart, jerusalemToday } from "../utils/jerusalemDay";
import useDialogFocus from "../components/a11y/useDialogFocus";
import {
  FOCUS_RING_CLS,
  RESPONSIVE_SHELL_CLS,
  RESPONSIVE_TABLE_CLS,
  SCROLL_REGION_CLS,
  TOUCH_TARGET_CLS,
} from "../components/designSystem/designTokens";
import { useViewportTier } from "../components/designSystem/useViewportTier";


// PATCH /tickets/{id} and POST /tickets/merge answer without the list-only fields (only the list route fills those), so
// spreading the answer over a cached row wiped the AI-review badge (robot link, confidence, proposed action) until the next
// refetch. The same shape hid the Digital Employee cell (FULL-0049): digital_employee is filled by the list route alone, so
// an inline technician/status/company change blanked it. The row's own values are kept.
const LIST_ONLY_ROW_FIELDS = ["digital_employee"];

const mergeServerTicket = (row, serverTicket) => {
  const merged = { ...row, ...serverTicket };
  for (const key of Object.keys(row)) {
    if (key.startsWith("pending_ai_") || LIST_ONLY_ROW_FIELDS.includes(key)) merged[key] = row[key];
  }
  return merged;
};

// Same period boundaries as the Reports summary that links here (week = since Sunday, month = since the 1st), not a rolling 7 / 30 days.
const PERIOD_DATES = {
  today: () => ({ opened_after: jerusalemPeriodStart("today"), opened_before: jerusalemToday() }),
  week:  () => ({ opened_after: jerusalemPeriodStart("week"),  opened_before: jerusalemToday() }),
  month: () => ({ opened_after: jerusalemPeriodStart("month"), opened_before: jerusalemToday() }),
};

// ─── constants ────────────────────────────────────────────────────────────────

// Badge colours only -- the wording lives in ticketStatusVocabulary, so this row's badge, its status
// picker and the filter below cannot drift apart (they had: this file's picker listed six arms with
// no `unassigned`, which rendered the status cell of every unassigned ticket blank).
const STATUS_COLOR = {
  open:        "bg-blue-100 text-blue-700",
  in_progress: "bg-yellow-100 text-yellow-700",
  pending:     "bg-purple-100 text-purple-700",
  waiting:     "bg-cyan-100 text-cyan-700",
  closed:      "bg-gray-100 text-gray-500",
  merged:      "bg-teal-100 text-teal-700",
};

const FILTER_STATUS_OPTIONS = [
  { value: "all_open",    label: "כל הפתוחות" },
  { value: "all",         label: "כל הסטטוסים" },   // also what the "history" / "all tickets of this customer" links ask for
  // This filter's own subset of the scale ("כל הפתוחות" is what covers `open`); only the wording is shared.
  ...["in_progress", "pending", "waiting", "closed"].map((value) => ({ value, label: TICKET_STATUS_HE[value] })),
];

const PRIORITY_OPTIONS = [
  { value: "high",   label: "גבוהה",   dot: "bg-red-400" },
  { value: "medium", label: "בינונית", dot: "bg-orange-300" },
  { value: "low",    label: "נמוכה",   dot: "bg-gray-300" },
];

const statusColor = (s) => STATUS_COLOR[s] ?? "";

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const DEFAULT_PAGE_SIZE = 50;

const fmtDateTime = (iso) => {
  const utc = /Z|[+-]\d\d:\d\d$/.test(iso) ? iso : iso + "Z";
  const d = new Date(utc);
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const _staleInfo = (t) => {
  if (!t.updated_at || ["closed", "merged"].includes(t.status)) return null;
  const utc = /Z|[+-]\d\d:\d\d$/.test(t.updated_at) ? t.updated_at : t.updated_at + "Z";
  const ageDays = (Date.now() - new Date(utc).getTime()) / 86400000;
  if (t.status === "waiting") {
    if (ageDays > 2) return { label: `ממתין ללקוח ${Math.floor(ageDays)}י`, cls: "bg-cyan-50 text-cyan-700 border border-cyan-200" };
    return null;
  }
  if (ageDays > 7) return { label: "בפיגור", cls: "bg-amber-50 text-amber-700 border border-amber-200" };
  return null;
};

const AI_CATEGORY_OPTIONS = [
  { value: "Email",    label: "Email / M365" },
  { value: "Network",  label: "רשת" },
  { value: "Hardware", label: "חומרה" },
  { value: "Software", label: "תוכנה" },
  { value: "Security", label: "אבטחה" },   // ai_service._CATEGORIES has it; without the option those tickets could not be filtered
  { value: "Other",    label: "אחר" },
];

const AI_CATEGORY_STYLE = {
  Email:    "bg-orange-50 text-orange-700 border-orange-200",
  Network:  "bg-blue-50 text-blue-700 border-blue-200",
  Hardware: "bg-gray-100 text-gray-600 border-gray-300",
  Software: "bg-green-50 text-green-700 border-green-200",
  Security: "bg-red-50 text-red-700 border-red-200",
  Other:    "bg-purple-50 text-purple-700 border-purple-200",
};

const EMPTY_FILTERS = {
  search: "",
  status: "all_open",
  company_id: "",
  technician_id: "",
  priority: "",
  sla_breach: "",           // "" | "true" | "false"
  has_customer_reply: false,
  has_pending_ai_review: false,
  ai_category: "",
  opened_after: "",
  opened_before: "",
  closed_after: "",             // the Reports summary counts "closed today / this week / this month" by closed_at
  closed_before: "",
  sender_email: "",
};

// ─── sidebar section wrapper ──────────────────────────────────────────────────

function Section({ title, children, className = "" }) {
  return (
    <div className={className}>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{title}</p>
      {children}
    </div>
  );
}

// ─── sortable column header ───────────────────────────────────────────────────
//
// The six sortable columns used to be `<th onClick>`: reachable with a mouse only, and silent to a
// screen reader about which column currently orders the list. The sort action is a real button
// inside the header cell, and the cell carries `aria-sort` so the current order is announced.
function SortableHeader({ field, label, sortField, sortDirection, onSort, className = "", style }) {
  const active = sortField === field;
  const direction = active && sortDirection === "asc" ? "ascending" : active ? "descending" : "none";
  return (
    <th scope="col" aria-sort={direction} className={className} style={style}>
      <button
        type="button"
        onClick={() => onSort(field)}
        aria-label={`מיין לפי ${label}`}
        className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} inline-flex w-full items-center gap-1 text-right`}
      >
        <span>{label}</span>
        <span aria-hidden="true">{active ? (sortDirection === "asc" ? "▲" : "▼") : ""}</span>
      </button>
    </th>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export default function TicketsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [showForm, setShowForm]               = useState(false);
  const [form, setForm]                       = useState(EMPTY_TICKET_FORM);
  const [filters, setFilters]                 = useState(EMPTY_FILTERS);
  const [debouncedSearch, setDbSearch]        = useState("");
  const [page, setPage]                       = useState(0);
  const [pageSize, setPageSize]               = useState(
    () => Number(localStorage.getItem("tickets_page_size") || DEFAULT_PAGE_SIZE)
  );
  const [sortField,     setSortField]         = useState('opened_at');
  const [sortDirection, setSortDirection]     = useState('desc');
  const [selectedTickets, setSelectedTickets] = useState(new Set());
  const [updatingCompanyTicketId, setUpdatingCompanyTicketId] = useState(null);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showBulkCloseConfirm, setShowBulkCloseConfirm] = useState(false);
  const [showBulkAssignDialog, setShowBulkAssignDialog] = useState(false);
  const [bulkAssignTechnicianId, setBulkAssignTechnicianId] = useState("");
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [showSplitDialog, setShowSplitDialog] = useState(false);
  const [splitForm, setSplitForm]             = useState({ new_subject: "", note: "", technician_id: "" });
  const [inlineError, setInlineError]         = useState(null);
  // PROD-UX-077: below the desktop breakpoint the filter rail stacks in front of the queue, so it
  // starts collapsed behind a named disclosure. The filters themselves are unchanged and every
  // one of them is still reachable; only their presentation order changes.
  const viewportTier = useViewportTier();
  const filtersCollapsed = viewportTier !== "desktop";
  const [filtersOpen, setFiltersOpen] = useState(false);
  const showFilterPanel = !filtersCollapsed || filtersOpen;
  const tableScrollRegion = viewportTier !== "mobile";
  // Destructive and multi-ticket dialogs own keyboard focus while open (PROD-UX-076).
  const bulkDeleteDialogRef = useRef(null);
  const bulkCloseDialogRef = useRef(null);
  const bulkAssignDialogRef = useRef(null);
  const mergeDialogRef = useRef(null);
  const splitDialogRef = useRef(null);
  useDialogFocus(bulkDeleteDialogRef, {
    active: showBulkDeleteConfirm,
    onClose: () => setShowBulkDeleteConfirm(false),
  });
  useDialogFocus(bulkCloseDialogRef, {
    active: showBulkCloseConfirm,
    onClose: () => setShowBulkCloseConfirm(false),
  });
  useDialogFocus(bulkAssignDialogRef, {
    active: showBulkAssignDialog,
    onClose: () => {
      setShowBulkAssignDialog(false);
      setBulkAssignTechnicianId("");
    },
  });
  useDialogFocus(mergeDialogRef, {
    active: showMergeDialog,
    onClose: () => setShowMergeDialog(false),
  });
  useDialogFocus(splitDialogRef, {
    active: showSplitDialog,
    onClose: () => setShowSplitDialog(false),
  });
  // A different row's inline edit failing within the 4s window used to have its timer clear THIS
  // row's still-fresh error message early -- one shared banner, but the timer wasn't per-message.
  const inlineErrorTimer = useRef(null);
  const { showOnlyMine }                      = useFilterMine();
  const { technicianId: myTechnicianId, isError: myTechnicianLookupFailed } = useTechnicianLookup();
  const { addNotification }                   = useNotification();

  const [searchParams] = useSearchParams();

  // True once the user actually touches a filter control (as opposed to the restore
  // effect below setting filters programmatically) — lets the page-reset effect tell
  // a real filter edit apart from restoring list state from a "from=" return URL.
  const userEditedListRef = useRef(false);

  // Restore filters/sort/page from the URL when landing here with state to reconstruct:
  // either the status/period deep-link from Reports, or the full "from=" return URL
  // that TicketDetail's "back to list" / Previous-Next / refresh echo back (see
  // buildReturnTo below) so the user returns to the exact list they left.
  useEffect(() => {
    const next = {};
    const search          = searchParams.get("search");
    const status           = searchParams.get("status");
    const companyId        = searchParams.get("company_id");
    const technicianId     = searchParams.get("technician_id");
    const priority         = searchParams.get("priority");
    const slaBreach        = searchParams.get("sla_breach");
    const hasCustomerReply     = searchParams.get("has_customer_reply");
    const hasPendingAiReview   = searchParams.get("has_pending_ai_review");
    const aiCategory           = searchParams.get("ai_category");
    const openedAfter      = searchParams.get("opened_after");
    const openedBefore     = searchParams.get("opened_before");
    const closedAfter      = searchParams.get("closed_after");
    const closedBefore     = searchParams.get("closed_before");
    const period           = searchParams.get("period");
    const senderEmail      = searchParams.get("sender_email");

    if (search)           next.search = search;
    if (status)           next.status = status;
    if (companyId)         next.company_id = companyId;
    if (technicianId)      next.technician_id = technicianId;
    if (priority)          next.priority = priority;
    if (slaBreach)         next.sla_breach = slaBreach;
    if (hasCustomerReply)      next.has_customer_reply = hasCustomerReply === "true";
    if (hasPendingAiReview)    next.has_pending_ai_review = hasPendingAiReview === "true";
    if (aiCategory)            next.ai_category = aiCategory;
    if (openedAfter)       next.opened_after = openedAfter;
    if (openedBefore)      next.opened_before = openedBefore;
    if (closedAfter)       next.closed_after = closedAfter;
    if (closedBefore)      next.closed_before = closedBefore;
    if (senderEmail)       next.sender_email = senderEmail;
    if (period && PERIOD_DATES[period]) {
      const range = PERIOD_DATES[period]();
      if (status === "closed") {
        // "closed in the period" is by closed_at, exactly as the Reports summary counts it (a ticket opened last month and closed today counts)
        next.closed_after = range.opened_after;
        next.closed_before = range.opened_before;
      } else {
        Object.assign(next, range);
      }
    }

    if (Object.keys(next).length) setFilters((f) => ({ ...f, ...next }));

    const sort = searchParams.get("sort");
    const dir = searchParams.get("dir");
    if (sort) setSortField(sort);
    if (dir === "asc" || dir === "desc") setSortDirection(dir);

    const restoredPage = Number(searchParams.get("page"));
    if (restoredPage > 0) setPage(restoredPage);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // debounce search input: only trigger query after 350 ms idle
  useEffect(() => {
    const t = setTimeout(() => setDbSearch(filters.search), 350);
    return () => clearTimeout(t);
  }, [filters.search]);

  // Reset to first page on a real filter/search edit — but not on the programmatic
  // filters update the restore effect above performs on mount, which carries its own
  // (possibly non-zero) restored page that must survive this.
  useEffect(() => {
    if (!userEditedListRef.current) return;
    setPage(0);
  }, [filters, debouncedSearch]);

  const setFilter = useCallback(
    (key, value) => {
      userEditedListRef.current = true;
      // A close-date range only makes sense with the "closed" status: left behind after the status is changed it emptied the list (open
      // tickets have no closed_at) while the sidebar looked as if it filtered by something else.
      setFilters((f) => ({ ...f, [key]: value, ...(key === "status" && value !== "closed" ? { closed_after: "", closed_before: "" } : {}) }));
    },
    []
  );

  const clearFilters = () => {
    userEditedListRef.current = true;
    setFilters(EMPTY_FILTERS);
    setDbSearch("");
  };

  const handleSort = (field) => {
    if (sortField !== field)          { setSortField(field); setSortDirection('asc'); }
    else if (sortDirection === 'asc')   setSortDirection('desc');
    else                              { setSortField('opened_at'); setSortDirection('desc'); }
  };

  const applySort = (list) => {
    if (!sortField) return list;
    return [...list].sort((a, b) => {
      let valA, valB;
      switch (sortField) {
        case 'sender':
          valA = a.sender_name || '';
          valB = b.sender_name || '';
          break;
        case 'company':
          valA = a.company?.name || '';
          valB = b.company?.name || '';
          break;
        case 'technician':
          valA = a.technician ? `${a.technician.first_name} ${a.technician.last_name}` : 'ת';
          valB = b.technician ? `${b.technician.first_name} ${b.technician.last_name}` : 'ת';
          break;
        case 'status':
          valA = a.status || '';
          valB = b.status || '';
          break;
        case 'opened_at':
          return sortDirection === 'asc'
            ? new Date(a.opened_at) - new Date(b.opened_at)
            : new Date(b.opened_at) - new Date(a.opened_at);
        case 'updated_at':
          return sortDirection === 'asc'
            ? new Date(a.updated_at) - new Date(b.updated_at)
            : new Date(b.updated_at) - new Date(a.updated_at);
        default:
          return 0;
      }
      return sortDirection === 'asc'
        ? valA.localeCompare(valB, 'he')
        : valB.localeCompare(valA, 'he');
    });
  };

  // The ▲/▼ marker moved into SortableHeader with the button and aria-sort (PROD-UX-076).

  const activeFilterCount = Object.entries(filters).filter(
    ([k, v]) => v !== "" && v !== "all_open" && v !== false && k !== "search"
  ).length + (debouncedSearch ? 1 : 0);

  // build API params from filters
  const apiParams = {
    ...(debouncedSearch                                           && { search: debouncedSearch }),
    ...(filters.status && filters.status !== "all_open" && filters.status !== "all" && { status: filters.status }),
    // "All open" is filtered server-side so the 2000-row cap applies to open tickets only;
    // filtering closed ones out client-side let old open tickets fall off the newest-2000 window.
    ...(filters.status === "all_open"                            && { open_only: true }),
    ...(filters.priority             && { priority: filters.priority }),
    ...(filters.company_id           && { company_id: filters.company_id }),
    ...(showOnlyMine && myTechnicianId
      ? { technician_id: myTechnicianId }
      : (filters.technician_id && { technician_id: filters.technician_id })),
    ...(filters.sla_breach !== ""    && { sla_breach: filters.sla_breach === "true" }),
    ...(filters.has_customer_reply       && { has_customer_reply: true }),
    ...(filters.has_pending_ai_review    && { has_pending_ai_review: true }),
    ...(filters.ai_category              && { ai_category: filters.ai_category }),
    ...(filters.opened_after         && { opened_after: jerusalemDayBound(filters.opened_after, "start") }),
    ...(filters.opened_before        && { opened_before: jerusalemDayBound(filters.opened_before, "end") }),
    ...(filters.closed_after         && { closed_after: jerusalemDayBound(filters.closed_after, "start") }),
    ...(filters.closed_before        && { closed_before: jerusalemDayBound(filters.closed_before, "end") }),
    ...(filters.sender_email         && { sender_email: filters.sender_email }),
    limit: 2000,
  };

  // ─── queries ────────────────────────────────────────────────────────────────

  // "Only mine" is on but the technician id is not known yet (the technician list is still loading, or failed): the request used
  // to go out WITHOUT the technician filter, so the page said "mine only" while listing (and offering bulk close / delete /
  // assign on) everybody's tickets. The Dashboard shows nothing in that state; hold the query the same way.
  const minePending = showOnlyMine && !myTechnicianId;

  const { data: tickets = [], isLoading, isFetching, isError: listFailed, refetch: refetchList } = useQuery({
    queryKey: ["tickets", apiParams],
    queryFn: () => ticketsApi.list(apiParams),
    enabled: !minePending,
    // v5 spelling (keepPreviousData: true is a v4 option and does nothing): a filter change keeps the old rows on screen
    // instead of swapping the table for the spinner. Never while the query is held: that would show the unfiltered list.
    placeholderData: minePending ? undefined : keepPreviousData,
    refetchInterval: 30_000,
  });

  const { data: companies   = [], isError: companiesFailed,   refetch: refetchCompanies } = useQuery({
    queryKey: ["companies"],
    queryFn: () => companiesApi.list(),
  });

  const { data: technicians = [], isError: techniciansFailed, refetch: refetchTechnicians } = useQuery({
    queryKey: ["technicians"],
    queryFn: () => techniciansApi.list(),
  });

  const { data: contacts    = [], isError: contactsFailed,    refetch: refetchContacts } = useQuery({
    queryKey: ["contacts"],
    queryFn: () => contactsApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: ticketsApi.create,
    onSuccess: (createdTicket) => {
      qc.invalidateQueries({ queryKey: ["tickets"] });
      setShowForm(false);
      setForm(EMPTY_TICKET_FORM);
      navigate(`/tickets/${createdTicket.id}`);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה ביצירת הקריאה", "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => ticketsApi.delete(id),
    onSuccess: (_, id) => {
      qc.setQueriesData({ queryKey: ["tickets"] }, (old) => old?.filter((t) => t.id !== id));
      qc.setQueryData(["tickets-dashboard"], (old) => old?.filter((t) => t.id !== id));
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה במחיקת הקריאה", "error"),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids) => ticketsApi.bulkDelete(ids),
    onSuccess: (_, ids) => {
      const idSet = new Set(ids);
      const remove = (old) => old?.filter((t) => !idSet.has(t.id));
      qc.setQueriesData({ queryKey: ["tickets"] }, remove);
      qc.setQueryData(["tickets-dashboard"], remove);
      setSelectedTickets(new Set());
      setShowBulkDeleteConfirm(false);
      addNotification("הקריאות המסומנות נמחקו");
    },
    onError: (error) => {
      addNotification(error?.response?.data?.detail || "שגיאה במחיקת הקריאות המסומנות");
    },
  });

  const bulkCloseMutation = useMutation({
    mutationFn: (ids) => ticketsApi.bulkUpdateStatus(ids, "closed"),
    onSuccess: async () => {
      setSelectedTickets(new Set());
      setShowBulkCloseConfirm(false);
      await qc.invalidateQueries({ queryKey: ["tickets"] });
      await qc.invalidateQueries({ queryKey: ["tickets-dashboard"] });
      addNotification("הקריאות המסומנות נסגרו בהצלחה");
    },
    onError: (error) => {
      addNotification(error?.response?.data?.detail || "שגיאה בסגירת הקריאות המסומנות");
    },
  });

  const bulkAssignMutation = useMutation({
    mutationFn: ({ ids, technicianId }) => ticketsApi.bulkAssign(ids, technicianId),
    onSuccess: async () => {
      setSelectedTickets(new Set());
      setShowBulkAssignDialog(false);
      setBulkAssignTechnicianId("");
      await qc.invalidateQueries({ queryKey: ["tickets"] });
      await qc.invalidateQueries({ queryKey: ["tickets-dashboard"] });
      addNotification("הקריאות המסומנות הוקצו בהצלחה");
    },
    onError: (error) => {
      addNotification(error?.response?.data?.detail || "שגיאה בהקצאת הקריאות המסומנות");
    },
  });

  const mergeMutation = useMutation({
    mutationFn: ({ primaryId, secondaryIds }) => ticketsApi.merge(primaryId, secondaryIds),
    onSuccess: (updatedPrimary, { secondaryIds }) => {
      const secSet = new Set(secondaryIds);
      const patch = (old) => old?.map((t) =>
        t.id === updatedPrimary.id ? mergeServerTicket(t, updatedPrimary) :
        secSet.has(t.id)           ? { ...t, status: "merged" }   : t
      );
      qc.setQueriesData({ queryKey: ["tickets"] }, patch);
      qc.setQueryData(["tickets-dashboard"], patch);
      setSelectedTickets(new Set());
      setShowMergeDialog(false);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה באיחוד הקריאות", "error"),
  });

  const splitMutation = useMutation({
    mutationFn: (data) => ticketsApi.split(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["tickets-dashboard"] });
      setSelectedTickets(new Set());
      setShowSplitDialog(false);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בפיצול הקריאה", "error"),
  });

  // A failed bulk action / merge / split kept its error until the next attempt, so reopening the dialog (often for a
  // DIFFERENT selection) showed the old failure. Clear each mutation's state when its dialog closes.
  const { reset: resetBulkDelete } = bulkDeleteMutation;
  const { reset: resetBulkClose } = bulkCloseMutation;
  const { reset: resetBulkAssign } = bulkAssignMutation;
  const { reset: resetMerge } = mergeMutation;
  const { reset: resetSplit } = splitMutation;
  // Not while the request is still in flight: reset() detaches the observer, so isPending would read false and re-enable
  // the toolbar buttons (a second bulk delete / merge) although the first request is still running. The effect re-runs
  // when the request settles and resets then.
  const { isPending: bulkDeletePending } = bulkDeleteMutation;
  const { isPending: bulkClosePending } = bulkCloseMutation;
  const { isPending: bulkAssignPending } = bulkAssignMutation;
  const { isPending: mergePending } = mergeMutation;
  const { isPending: splitPending } = splitMutation;
  useEffect(() => { if (!showBulkDeleteConfirm && !bulkDeletePending) resetBulkDelete(); }, [showBulkDeleteConfirm, bulkDeletePending, resetBulkDelete]);
  useEffect(() => { if (!showBulkCloseConfirm && !bulkClosePending) resetBulkClose(); }, [showBulkCloseConfirm, bulkClosePending, resetBulkClose]);
  useEffect(() => { if (!showBulkAssignDialog && !bulkAssignPending) resetBulkAssign(); }, [showBulkAssignDialog, bulkAssignPending, resetBulkAssign]);
  useEffect(() => { if (!showMergeDialog && !mergePending) resetMerge(); }, [showMergeDialog, mergePending, resetMerge]);
  useEffect(() => { if (!showSplitDialog && !splitPending) resetSplit(); }, [showSplitDialog, splitPending, resetSplit]);
  // None of the 3 bulk-action confirmation dialogs had an Escape handler -- a keyboard user had to
  // tab to the "ביטול" button to dismiss any of them.
  useEffect(() => {
    if (!showBulkDeleteConfirm && !showBulkCloseConfirm && !showBulkAssignDialog) return;
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      setShowBulkDeleteConfirm(false);
      setShowBulkCloseConfirm(false);
      setShowBulkAssignDialog(false);
      setBulkAssignTechnicianId("");
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showBulkDeleteConfirm, showBulkCloseConfirm, showBulkAssignDialog]);

  const inlineUpdate = useMutation({
    mutationFn: ({ id, data }) => ticketsApi.update(id, data),
    onMutate: async ({ id, data }) => {
      await qc.cancelQueries({ queryKey: ["tickets"] });
      const snapshot = qc.getQueriesData({ queryKey: ["tickets"] });
      // ["tickets-dashboard"] is a separate top-level key -- {queryKey: ["tickets"]} above does NOT
      // match it (partial-key matching compares the key array prefix), so it needs its own snapshot.
      const dashboardSnapshot = qc.getQueryData(["tickets-dashboard"]);
      const applyOpt = (old) => old?.map((t) => t.id === id ? { ...t, ...data } : t);
      qc.setQueriesData({ queryKey: ["tickets"] }, applyOpt);
      qc.setQueryData(["tickets-dashboard"], applyOpt);
      return { snapshot, dashboardSnapshot };
    },
    onSuccess: (updatedTicket) => {
      setInlineError(null);
      // Replace optimistic data with server-confirmed data (includes nested company/technician).
      const patch = (old) =>
        old?.map((t) => (t.id === updatedTicket.id ? mergeServerTicket(t, updatedTicket) : t));
      qc.setQueriesData({ queryKey: ["tickets"] }, patch);
      qc.setQueryData(["tickets-dashboard"], patch);
    },
    onError: (_err, _vars, context) => {
      if (context?.snapshot) {
        context.snapshot.forEach(([queryKey, data]) => qc.setQueryData(queryKey, data));
        qc.setQueryData(["tickets-dashboard"], context.dashboardSnapshot);
      }
      setInlineError("שגיאת רשת — העדכון נכשל");
      clearTimeout(inlineErrorTimer.current);
      inlineErrorTimer.current = setTimeout(() => setInlineError(null), 4000);
    },
  });

  const updateTicketCompany = useMutation({
    mutationFn: ({ ticketId, companyId }) => ticketsApi.update(ticketId, { company_id: companyId }),
    onMutate: async ({ ticketId, company }) => {
      setUpdatingCompanyTicketId(ticketId);
      await qc.cancelQueries({ queryKey: ["tickets"] });
      const snapshot = qc.getQueriesData({ queryKey: ["tickets"] });
      qc.setQueriesData({ queryKey: ["tickets"] }, (old) => old?.map((ticket) =>
        ticket.id === ticketId ? { ...ticket, company: company ?? null, company_id: company?.id ?? null } : ticket
      ));
      return { snapshot };
    },
    onSuccess: (updatedTicket) => {
      qc.setQueriesData({ queryKey: ["tickets"] }, (old) => old?.map((ticket) =>
        ticket.id === updatedTicket.id ? mergeServerTicket(ticket, updatedTicket) : ticket
      ));
      qc.setQueryData(["tickets-dashboard"], (old) => old?.map((ticket) =>
        ticket.id === updatedTicket.id ? mergeServerTicket(ticket, updatedTicket) : ticket
      ));
      addNotification("החברה עודכנה בהצלחה");
    },
    onError: (_error, _variables, context) => {
      context?.snapshot?.forEach(([queryKey, data]) => qc.setQueryData(queryKey, data));
      addNotification("שגיאה בעדכון החברה");
    },
    onSettled: (_data, _error, { ticketId }) => {
      setUpdatingCompanyTicketId((current) => current === ticketId ? null : current);
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["tickets-dashboard"] });
    },
  });

  const sortedTickets = applySort(tickets);
  const totalPages    = Math.ceil(sortedTickets.length / pageSize);
  const pagedTickets  = sortedTickets.slice(page * pageSize, (page + 1) * pageSize);
  // Same ordered id list as the visible rows below, carried into ticket detail so
  // Previous/Next can walk this exact filtered+sorted page without a round-trip here.
  const pagedTicketIds = pagedTickets.map((t) => t.id);

  // The list shrinks under the pager (a ticket is closed, merged or deleted, or the poll returns fewer rows): a page
  // past the end showed an empty table under "21-20 of 20" with no pager to get back. Wait for the first load, or a
  // page restored from the URL would be reset while the list is still empty.
  // Also not after a FAILED load: the list is empty then, and the clamp would wipe a page restored from the URL.
  useEffect(() => {
    if (isLoading || listFailed) return;
    if (page > 0 && page >= totalPages) setPage(Math.max(0, totalPages - 1));
  }, [isLoading, listFailed, page, totalPages]);

  // Bulk actions send the whole selection: a ticket ticked and then FILTERED out of the list (or closed/merged by
  // someone else) was still deleted/closed/assigned while the dialog counted it. Keep only tickets that are still in
  // the filtered list. Not just the current page: merge and split are built from the whole list, so selecting on one
  // page and acting from another keeps working.
  const listIdsKey = sortedTickets.map((t) => t.id).join(",");
  useEffect(() => {
    if (isLoading || listFailed) return;
    const visible = new Set(listIdsKey ? listIdsKey.split(",") : []);
    setSelectedTickets((prev) => {
      const kept = [...prev].filter((id) => visible.has(id));
      return kept.length === prev.size ? prev : new Set(kept);
    });
  }, [isLoading, listFailed, listIdsKey]);

  // The URL TicketDetail's "from=" carries back here (on "back to list", on Previous/
  // Next, and across a refresh, since it's embedded in TicketDetail's own address bar
  // URL) — encodes only the non-default parts of the current filter/sort/page state,
  // so returning reconstructs the exact list the user left instead of a reset one.
  const buildReturnTo = () => {
    const params = new URLSearchParams();
    if (filters.search)                                            params.set("search", filters.search);
    if (filters.status && filters.status !== EMPTY_FILTERS.status) params.set("status", filters.status);
    if (filters.company_id)                                        params.set("company_id", filters.company_id);
    if (filters.technician_id)                                     params.set("technician_id", filters.technician_id);
    if (filters.priority)                                          params.set("priority", filters.priority);
    if (filters.sla_breach)                                        params.set("sla_breach", filters.sla_breach);
    if (filters.has_customer_reply)                                params.set("has_customer_reply", "true");
    if (filters.has_pending_ai_review)                             params.set("has_pending_ai_review", "true");
    if (filters.ai_category)                                       params.set("ai_category", filters.ai_category);
    if (filters.opened_after)                                      params.set("opened_after", filters.opened_after);
    if (filters.opened_before)                                     params.set("opened_before", filters.opened_before);
    if (filters.closed_after)                                      params.set("closed_after", filters.closed_after);
    if (filters.closed_before)                                     params.set("closed_before", filters.closed_before);
    if (filters.sender_email)                                      params.set("sender_email", filters.sender_email);
    if (sortField && sortField !== "opened_at")                    params.set("sort", sortField);
    if (sortDirection && sortDirection !== "desc")                  params.set("dir", sortDirection);
    if (page > 0)                                                  params.set("page", String(page));
    const qs = params.toString();
    return `/tickets${qs ? `?${qs}` : ""}`;
  };
  const returnTo = buildReturnTo();

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate({
      ...form,
      technician_id: form.technician_id || null,
      company_id: form.company_id || null,
      contact_id: form.contact_id || null,
    });
  };

  // ─── render ──────────────────────────────────────────────────────────────────

  return (
    <>
    <div dir="rtl" className="flex flex-wrap gap-3 xl:gap-4 2xl:gap-5 items-start" style={{ minWidth: 0, overflowX: 'hidden' }}>

      {/* Header */}
      <div className="order-1 basis-full flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 xl:gap-3 min-w-0">
          <h1 className="text-xl xl:text-2xl font-bold text-gray-800">קריאות שירות</h1>
          {isFetching && (
            <div className="w-4 h-4 rounded-full border-2 border-brand-400 border-t-transparent animate-spin" />
          )}
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-brand-600 hover:bg-brand-700 text-white px-3 xl:px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          + קריאה חדשה
        </button>
      </div>

      {/* Results count + page size */}
      <div className="order-2 basis-full flex items-center justify-between gap-3 flex-wrap text-sm text-gray-500">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
        <span>
          {isLoading
          ? "טוען..."
          : listFailed && tickets.length === 0
            ? "שגיאה בטעינת הקריאות"
          : minePending
            ? "—"
          : tickets.length === 0
            ? "0 קריאות"
            : `${page * pageSize + 1}–${Math.min((page + 1) * pageSize, tickets.length)} מתוך ${tickets.length} קריאות`
        }
        </span>
        {/* PROD-UX-077: on tablet and phone the rail would otherwise push the queue below a
            dozen filter groups. The disclosure names the state and the applied-filter count. */}
        {filtersCollapsed && (
          <button
            type="button"
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            aria-controls="tickets-filter-panel"
            className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50`}
          >
            <span aria-hidden="true">☰</span>
            {filtersOpen ? "הסתר סננים" : "הצג סננים"}
            {activeFilterCount > 0 && (
              <span className="rounded-full bg-brand-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
        )}
        </div>
        <div className="flex items-center gap-3">
          {filters.sender_email && (
            <span className="flex items-center gap-1 text-xs bg-brand-50 text-brand-700 border border-brand-200 rounded-full px-2 py-0.5">
              שולח: {filters.sender_email}
              <button
                type="button"
                onClick={() => setFilter("sender_email", "")}
                aria-label="הסר את סינון השולח"
                className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} me-1 rounded hover:text-red-500`}
              >✕</button>
            </span>
          )}
          {activeFilterCount > 0 && (
            <span className="text-xs text-brand-600">
              {activeFilterCount} פילטרים פעילים
            </span>
          )}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-400">הצג:</span>
            <select
              value={pageSize}
              aria-label="מספר קריאות בעמוד"
              onChange={(e) => {
                const v = Number(e.target.value);
                setPageSize(v);
                setPage(0);
                localStorage.setItem("tickets_page_size", String(v));
              }}
              className="border rounded px-1.5 py-0.5 text-xs text-gray-600 bg-white cursor-pointer"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span className="text-xs text-gray-400">בדף</span>
          </div>
        </div>
      </div>

      {/* ── SIDEBAR ──────────────────────────────────────────────────────────── */}
      {showFilterPanel && (
      <aside
        id="tickets-filter-panel"
        data-testid="tickets-filter-panel"
        className="order-3 lg:order-4 w-full lg:w-48 xl:w-52 shrink-0 bg-white rounded-xl border shadow-sm p-3 xl:p-4 space-y-4 xl:space-y-5 lg:sticky lg:top-4"
      >

        {/* Search */}
        <Section title="חיפוש">
          <div className="relative">
            <input
              type="text"
              placeholder="חיפוש חופשי..."
              aria-label="חיפוש חופשי"
              value={filters.search}
              onChange={(e) => setFilter("search", e.target.value)}
              className="w-full border rounded-lg px-3 py-1.5 text-sm pe-8"
            />
            {filters.search && (
              <button
                type="button"
                onClick={() => setFilter("search", "")}
                aria-label="נקה את החיפוש"
                className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} absolute end-2 top-1/2 -translate-y-1/2 rounded text-gray-400 hover:text-gray-600 text-xs`}
              >
                ✕
              </button>
            )}
          </div>
        </Section>

        <hr className="border-gray-100" />

        {/* Status */}
        <Section title="סטטוס">
          <div className="space-y-1">
            {FILTER_STATUS_OPTIONS.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="radio"
                  name="status"
                  value={opt.value}
                  checked={filters.status === opt.value}
                  onChange={() => setFilter("status", opt.value)}
                  className="accent-brand-600"
                />
                <span className="text-sm text-gray-700 group-hover:text-brand-600">{opt.label}</span>
              </label>
            ))}
          </div>
        </Section>

        <hr className="border-gray-100" />

        {/* Company */}
        <Section title="חברה">
          {companiesFailed && companies.length === 0 ? (
            // a failed lookup is not "no companies": an empty picker during an outage silently removes the filtering choice
            <div role="alert" className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg space-y-1">
              <p>לא ניתן לטעון את רשימת החברות</p>
              <button onClick={() => refetchCompanies()} className="text-brand-600 hover:underline">נסה שוב</button>
            </div>
          ) : (
          <select
            value={filters.company_id}
            onChange={(e) => setFilter("company_id", e.target.value)}
            className="w-full border rounded-lg px-2 py-1.5 text-sm"
          >
            <option value="">הכל</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          )}
        </Section>

        <hr className="border-gray-100" />

        {/* Technician */}
        <Section title="טכנאי">
          {techniciansFailed && technicians.length === 0 ? (
            <div role="alert" className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg space-y-1">
              <p>לא ניתן לטעון את רשימת הטכנאים</p>
              <button onClick={() => refetchTechnicians()} className="text-brand-600 hover:underline">נסה שוב</button>
            </div>
          ) : (
          <select
            value={filters.technician_id}
            onChange={(e) => setFilter("technician_id", e.target.value)}
            disabled={showOnlyMine}
            title={showOnlyMine ? "מסנן 'רק שלי' פעיל" : undefined}
            className="w-full border rounded-lg px-2 py-1.5 text-sm disabled:opacity-50"
          >
            <option value="">הכל</option>
            {technicians.map((t) => (
              <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>
            ))}
          </select>
          )}
        </Section>

        <hr className="border-gray-100" />

        {/* Priority */}
        <Section title="עדיפות">
          <div className="space-y-1">
            <label className="flex items-center gap-2 cursor-pointer group">
              <input
                type="radio"
                name="priority"
                value=""
                checked={filters.priority === ""}
                onChange={() => setFilter("priority", "")}
                className="accent-brand-600"
              />
              <span className="text-sm text-gray-700 group-hover:text-brand-600">הכל</span>
            </label>
            {PRIORITY_OPTIONS.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="radio"
                  name="priority"
                  value={opt.value}
                  checked={filters.priority === opt.value}
                  onChange={() => setFilter("priority", opt.value)}
                  className="accent-brand-600"
                />
                <span className={`inline-block w-2 h-2 rounded-full ${opt.dot}`} />
                <span className="text-sm text-gray-700 group-hover:text-brand-600">{opt.label}</span>
              </label>
            ))}
          </div>
        </Section>

        <hr className="border-gray-100" />

        {/* SLA */}
        <Section title="SLA">
          <div className="space-y-1">
            {[
              { value: "",      label: "הכל" },
              { value: "false", label: "תקין" },
              { value: "true",  label: "חריגה" },
            ].map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="radio"
                  name="sla"
                  value={opt.value}
                  checked={filters.sla_breach === opt.value}
                  onChange={() => setFilter("sla_breach", opt.value)}
                  className="accent-brand-600"
                />
                <span className={`text-sm group-hover:text-brand-600 ${
                  opt.value === "true" ? "text-red-600 font-medium" : "text-gray-700"
                }`}>
                  {opt.label}
                </span>
              </label>
            ))}
          </div>
        </Section>

        <hr className="border-gray-100" />

        {/* AI Category */}
        <Section title="קטגוריית AI">
          <div className="space-y-1">
            <label className="flex items-center gap-2 cursor-pointer group">
              <input type="radio" name="ai_cat" value="" checked={filters.ai_category === ""} onChange={() => setFilter("ai_category", "")} className="accent-brand-600" />
              <span className="text-sm text-gray-700 group-hover:text-brand-600">הכל</span>
            </label>
            {AI_CATEGORY_OPTIONS.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer group">
                <input type="radio" name="ai_cat" value={opt.value} checked={filters.ai_category === opt.value} onChange={() => setFilter("ai_category", opt.value)} className="accent-brand-600" />
                <span className="text-sm text-gray-700 group-hover:text-brand-600">{opt.label}</span>
              </label>
            ))}
          </div>
        </Section>

        <hr className="border-gray-100" />

        {/* Customer reply */}
        <Section title="תגובת לקוח">
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={filters.has_customer_reply === true}
              onChange={(e) => setFilter("has_customer_reply", e.target.checked ? true : false)}
              className="accent-brand-600 w-3.5 h-3.5"
            />
            <span className="text-sm text-orange-600 font-medium group-hover:text-orange-700">
              ✉ לקוח השיב
            </span>
          </label>
        </Section>

        <hr className="border-gray-100" />

        {/* AI pending review */}
        <Section title="ניתוח AI">
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={filters.has_pending_ai_review === true}
              onChange={(e) => setFilter("has_pending_ai_review", e.target.checked ? true : false)}
              className="accent-violet-600 w-3.5 h-3.5"
            />
            <span className="text-sm text-violet-700 font-medium group-hover:text-violet-800">
              🤖 ממתין לסקירה
            </span>
          </label>
        </Section>

        <hr className="border-gray-100" />

        {/* Date range */}
        <Section title="תאריך פתיחה">
          <div className="space-y-2">
            <div>
              <label className="text-xs text-gray-400 block mb-0.5">מתאריך</label>
              <input
                type="date"
                value={filters.opened_after}
                onChange={(e) => setFilter("opened_after", e.target.value)}
                className="w-full border rounded-lg px-2 py-1.5 text-xs"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-0.5">עד תאריך</label>
              <input
                type="date"
                value={filters.opened_before}
                onChange={(e) => setFilter("opened_before", e.target.value)}
                className="w-full border rounded-lg px-2 py-1.5 text-xs"
              />
            </div>
          </div>
        </Section>

        <hr className="border-gray-100" />

        <Section title="תאריך סגירה">
          <div className="space-y-2">
            <div>
              <label className="text-xs text-gray-400 block mb-0.5">מתאריך</label>
              <input
                type="date"
                value={filters.closed_after}
                onChange={(e) => setFilter("closed_after", e.target.value)}
                className="w-full border rounded-lg px-2 py-1.5 text-xs"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-0.5">עד תאריך</label>
              <input
                type="date"
                value={filters.closed_before}
                onChange={(e) => setFilter("closed_before", e.target.value)}
                className="w-full border rounded-lg px-2 py-1.5 text-xs"
              />
            </div>
          </div>
        </Section>

        <hr className="border-gray-100" />

        {/* Clear */}
        <button
          onClick={clearFilters}
          disabled={activeFilterCount === 0}
          className="w-full text-xs text-brand-600 hover:text-brand-800 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          {activeFilterCount > 0 ? `נקה פילטרים (${activeFilterCount})` : "אין פילטרים פעילים"}
        </button>
      </aside>
      )}

      {/* ── MAIN CONTENT ─────────────────────────────────────────────────────── */}
      <div className="order-4 lg:order-3 flex-1 basis-full lg:basis-0 min-w-0 space-y-3 xl:space-y-4">

        {/* New ticket form */}
        {showForm && (companiesFailed || techniciansFailed || contactsFailed) && (
          // the form's pickers read the same lookups: an outage must not look like a form with nothing to choose
          <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-2.5 rounded-lg flex items-center justify-between gap-3 flex-wrap">
            <span>חלק מהרשימות (חברות / טכנאים / אנשי קשר) לא נטענו — ייתכן שחסרות אפשרויות בטופס</span>
            <button
              onClick={() => {
                if (companiesFailed) refetchCompanies();
                if (techniciansFailed) refetchTechnicians();
                if (contactsFailed) refetchContacts();
              }}
              className="text-brand-600 hover:underline flex-shrink-0"
            >
              נסה שוב
            </button>
          </div>
        )}
        {showForm && (
          <TicketCreateForm
            form={form}
            setForm={setForm}
            companies={companies}
            contacts={contacts}
            technicians={technicians}
            priorityOptions={PRIORITY_OPTIONS}
            createMutation={createMutation}
            onSubmit={handleSubmit}
            onCancel={() => { setShowForm(false); setForm(EMPTY_TICKET_FORM); }}
          />
        )}

        {/* Bulk action toolbar */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5 flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-blue-700">
            {selectedTickets.size === 0 ? "לא נבחרו קריאות" : `נבחרו ${selectedTickets.size} קריאות`}
          </span>
          <button
            onClick={() => setShowBulkDeleteConfirm(true)}
            disabled={selectedTickets.size === 0 || bulkDeleteMutation.isPending}
            className="text-xs bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            מחק
          </button>
          <button
            onClick={() => setShowMergeDialog(true)}
            disabled={selectedTickets.size < 2 || mergeMutation.isPending}
            title={selectedTickets.size < 2 ? "יש לבחור לפחות 2 קריאות לאיחוד" : ""}
            className="text-xs bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            איחוד
          </button>
          <button
            onClick={() => {
              const t = sortedTickets.find((t) => selectedTickets.has(t.id));
              if (t) {
                setSplitForm({ new_subject: `FW: ${t.title}`, note: "", technician_id: "" });
                setShowSplitDialog(true);
              }
            }}
            disabled={selectedTickets.size !== 1 || splitMutation.isPending}
            title={selectedTickets.size !== 1 ? "יש לבחור קריאה אחת בדיוק לפיצול" : ""}
            className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            פצל
          </button>
          <button
            onClick={() => setShowBulkCloseConfirm(true)}
            disabled={selectedTickets.size === 0 || bulkCloseMutation.isPending}
            className="text-xs bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            סגור
          </button>
          <button
            onClick={() => setShowBulkAssignDialog(true)}
            disabled={selectedTickets.size === 0 || bulkAssignMutation.isPending}
            className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            הקצה לנציג
          </button>
          {myTechnicianId && (
            <button
              onClick={() => bulkAssignMutation.mutate({ ids: [...selectedTickets], technicianId: myTechnicianId })}
              disabled={selectedTickets.size === 0 || bulkAssignMutation.isPending}
              className="text-xs bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title="שבץ את כל הקריאות שנבחרו אליי"
            >
              👋 קח אליי
            </button>
          )}
          <button
            onClick={() => setSelectedTickets(new Set())}
            disabled={selectedTickets.size === 0}
            className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            בטל בחירה
          </button>
        </div>

        {inlineError && (
          <div className="mb-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
            {inlineError}
          </div>
        )}

        {/* Tickets table */}
        {isLoading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
          </div>
        ) : listFailed && tickets.length === 0 ? (
          // a failed request is not "no tickets": an empty inbox during an outage reads as all-clear
          <div role="alert" className="text-center py-20 bg-white rounded-xl border text-red-600">
            <p className="text-lg font-medium">לא ניתן לטעון את הקריאות</p>
            <button onClick={() => refetchList()} className="mt-2 text-sm text-brand-600 hover:underline">
              נסה שוב
            </button>
          </div>
        ) : minePending && myTechnicianLookupFailed ? (
          // a failed technician-identity lookup used to read the same as "still identifying you"
          // forever, which reads as "no linked technician" rather than "the lookup itself failed".
          <div role="alert" className="text-center py-20 bg-white rounded-xl border text-red-600">
            <p className="text-lg font-medium">לא ניתן לאמת את זהות הטכנאי — לא ניתן להציג "רק שלי" כרגע</p>
          </div>
        ) : minePending ? (
          // the request is held until the technician id is known: say so instead of showing an all-clear empty inbox
          <div role="status" className="text-center py-20 bg-white rounded-xl border text-gray-500">
            <p className="text-lg font-medium">מזהה את הטכנאי שלך...</p>
            <p className="mt-1 text-sm">אם ההודעה נשארת, אין לחשבון שלך טכנאי מקושר — כבה את הסינון "רק שלי" כדי לראות את כל הקריאות.</p>
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border text-gray-400">
            <p className="text-lg font-medium">לא נמצאו קריאות</p>
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} className="mt-2 text-sm text-brand-600 hover:underline">
                נקה פילטרים
              </button>
            )}
          </div>
        ) : (
          <div
            className={`bg-white rounded-xl shadow-sm border overflow-x-auto ${RESPONSIVE_SHELL_CLS}${
              tableScrollRegion ? ` ${SCROLL_REGION_CLS}` : ""
            }`}
            role={tableScrollRegion ? "region" : undefined}
            aria-label={tableScrollRegion ? "טבלת קריאות — ניתן לגלילה אופקית" : undefined}
            tabIndex={tableScrollRegion ? 0 : undefined}
          >
            <table className={`w-full table-fixed text-xs xl:text-sm ${RESPONSIVE_TABLE_CLS}`} style={{ width: '100%' }}>
              <thead className="bg-gray-50 border-b text-gray-500 text-xs uppercase tracking-wide">
                <tr>
                  {/* No select-all: tickets may only be selected one at a time */}
                  <th className="px-2 xl:px-3 py-2.5 xl:py-3" style={{ width: 36 }} />
                  <th className="text-right px-2 xl:px-3 py-2.5 xl:py-3" style={{ width: 55 }}>#</th>
                  <th className="text-right px-2 xl:px-3 py-2.5 xl:py-3" style={{ width: 400 }}>כותרת</th>
                  <SortableHeader field="sender" label="שולח" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} className="hidden 2xl:table-cell text-right px-2 xl:px-3 py-2.5 xl:py-3 select-none hover:bg-gray-100 whitespace-nowrap" style={{ width: 160 }} />
                  <SortableHeader field="company" label="חברה" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} className="text-right px-2 xl:px-3 py-2.5 xl:py-3 select-none hover:bg-gray-100 whitespace-nowrap" style={{ width: 130 }} />
                  <SortableHeader field="technician" label="טכנאי" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} className="text-right px-2 xl:px-3 py-2.5 xl:py-3 select-none hover:bg-gray-100 whitespace-nowrap" style={{ width: 140 }} />
                  {/* The ticket's Digital Employee sits next to the human owner: both are "who is on this", and the queue is
                      where the routing decision is made. Unlike שולח / עדכון אחרון it is not hidden at narrow widths —
                      which employee (if any) owns a ticket is the fact this screen was missing. */}
                  <th className="text-right px-2 xl:px-3 py-2.5 xl:py-3 whitespace-nowrap" style={{ width: 130 }}>עובד דיגיטלי</th>
                  <SortableHeader field="status" label="סטטוס" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} className="text-right px-2 xl:px-3 py-2.5 xl:py-3 select-none hover:bg-gray-100 whitespace-nowrap" style={{ width: 100 }} />
                  <th className="text-right px-2 xl:px-3 py-2.5 xl:py-3" style={{ width: 80 }}>SLA</th>
                  <th className="text-right px-2 xl:px-3 py-2.5 xl:py-3" style={{ width: 135 }}>מועד יעד</th>
                  <SortableHeader field="opened_at" label="תאריך פתיחה" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} className="text-right px-2 xl:px-3 py-2.5 xl:py-3 select-none hover:bg-gray-100 whitespace-nowrap" style={{ width: 110 }} />
                  <SortableHeader field="updated_at" label="עדכון אחרון" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} className="hidden 2xl:table-cell text-right px-2 xl:px-3 py-2.5 xl:py-3 select-none hover:bg-gray-100 whitespace-nowrap" style={{ width: 110 }} />
                  <th className="px-1 py-2.5 xl:py-3" style={{ width: 36 }} />
                </tr>
              </thead>
              <tbody>
                {pagedTickets.map((t) => (
                  <tr key={t.id} className={`border-b last:border-0 hover:bg-gray-50 transition-colors ${isTicketOverdue(t) ? "bg-red-50 hover:bg-red-100" : selectedTickets.has(t.id) ? "bg-blue-50" : ""}`}>
                    <td className="px-2 xl:px-3 py-2.5 xl:py-3" data-label="בחירה">
                      <input
                        type="checkbox"
                        className="accent-brand-600 w-3.5 h-3.5"
                        checked={selectedTickets.has(t.id)}
                        aria-label={`בחר קריאה #${t.ticket_number} — ${t.title}`}
                        onChange={(e) => {
                          setSelectedTickets((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(t.id);
                            else next.delete(t.id);
                            return next;
                          });
                        }}
                      />
                    </td>
                    <td className="px-2 xl:px-3 py-2.5 xl:py-3 text-gray-400 font-mono text-xs" style={{ overflow: 'hidden' }} data-label="מספר">
                      <div className="inline-flex items-center gap-1 whitespace-nowrap">
                        <span>{t.ticket_number}</span>
                        {ticketHasAttachments(t) && (
                          <span title={`${attachmentCount(t)} קבצים מצורפים`} className="inline-flex items-center gap-0.5 text-slate-400 text-sm leading-none flex-shrink-0">
                            📎
                            <span className="text-[10px] font-semibold leading-none">{attachmentCount(t)}</span>
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 xl:px-3 py-2.5 xl:py-3 font-medium" style={{ overflow: 'hidden' }} data-label="כותרת">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Link
                          to={`/tickets/${t.id}?from=${encodeURIComponent(returnTo)}`}
                          state={{ ticketIds: pagedTicketIds }}
                          className="text-brand-600 hover:underline truncate"
                          title={t.title}
                        >
                          {t.title}
                        </Link>
                        {t.sender_email === "system@prodigy.internal" && (
                          <span title="בדיקה מתוזמנת — לא קריאת לקוח" className="text-xs text-violet-600 font-medium bg-violet-50 border border-violet-200 rounded px-1 py-0.5 flex-shrink-0">🕐 מתוזמן</span>
                        )}
                        {t.priority === "high" && (
                          <span title="עדיפות גבוהה" className="text-xs text-red-600 font-semibold bg-red-50 border border-red-200 rounded-full px-2 py-0.5 flex-shrink-0">⚠ גבוהה</span>
                        )}
                        {t.has_customer_reply && (
                          <span title="הלקוח השיב למייל" className="text-orange-500 text-sm leading-none flex-shrink-0">✉</span>
                        )}
                        {/* The provenance of the analysis this row's AI chips describe. It heads that group so
                            it is read before the confidence figure it qualifies: on the pilot default (no
                            ANALYSIS_PROVIDER) the pending run is the deterministic mock, whose confidence is
                            derived from ticket richness rather than by any AI. */}
                        {degradedAnalysisLabel(t.pending_ai_degraded) && (
                          <span
                            data-testid={`ticket-ai-degraded-${t.id}`}
                            className="text-[10px] font-medium flex-shrink-0 leading-none bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-1.5 py-0.5"
                          >{degradedAnalysisLabel(t.pending_ai_degraded)}</span>
                        )}
                        {t.pending_ai_run_status && (
                          <Link
                            to={`/tickets/${t.id}?tab=ai-diagnosis`}
                            title={
                              (t.pending_ai_run_status === "awaiting_technician_review" || t.pending_ai_run_status === "awaiting_approval")
                                ? `ניתוח AI ממתין לסקירה${t.pending_ai_confidence != null ? ` (ביטחון: ${t.pending_ai_confidence}%)` : ""} — לחץ לסקירה`
                                : t.pending_ai_run_status === "paused"
                                  ? "ריצת AI הושהתה — לחץ להמשך"
                                  : "ניתוח AI בתהליך"
                            }
                            className={`text-sm leading-none flex-shrink-0 ${
                              (t.pending_ai_run_status === "awaiting_technician_review" || t.pending_ai_run_status === "awaiting_approval")
                                ? "text-orange-500"
                                : t.pending_ai_run_status === "paused"
                                  ? "text-gray-400"
                                  : "text-violet-500"
                            }`}
                            onClick={(e) => e.stopPropagation()}
                          >🤖</Link>
                        )}
                        {t.pending_ai_run_status && (t.pending_ai_run_status === "awaiting_technician_review" || t.pending_ai_run_status === "awaiting_approval") && t.pending_ai_confidence != null && (
                          <span
                            className={`text-[10px] font-semibold flex-shrink-0 leading-none ${
                              t.pending_ai_confidence >= 70 ? "text-green-600"
                              : t.pending_ai_confidence >= 50 ? "text-amber-500"
                              : "text-red-500"
                            }`}
                            title={`ביטחון AI: ${t.pending_ai_confidence}%`}
                          >{t.pending_ai_confidence}%</span>
                        )}
                        {t.pending_ai_risk_level === "red" && (
                          <span className="text-[10px] font-semibold text-red-600 flex-shrink-0 leading-none" title="סיכון AI: גבוה">⚠</span>
                        )}
                        {t.pending_ai_action_type && (t.pending_ai_run_status === "awaiting_technician_review" || t.pending_ai_run_status === "awaiting_approval") && (
                          <span
                            className="text-[9px] font-semibold flex-shrink-0 leading-none bg-violet-50 text-violet-600 border border-violet-200 rounded px-1 py-0.5"
                            title={`פעולה מוצעת: ${t.pending_ai_action_type}${t.pending_ai_likely_issue ? ` · ${t.pending_ai_likely_issue.slice(0, 80)}` : ""}`}
                          >{{
                            "close_ticket": "סגור",
                            "send_customer_message": "מייל",
                            "add_comment": "הערה",
                            "escalate": "הסלם",
                            "request_information": "בקש מידע",
                          }[t.pending_ai_action_type] || t.pending_ai_action_type}</span>
                        )}
                        {(t.pending_ai_run_status === "awaiting_technician_review" || t.pending_ai_run_status === "awaiting_approval") && t.pending_ai_run_started_at && (() => {
                          const hoursWaiting = (Date.now() - new Date(t.pending_ai_run_started_at).getTime()) / 3_600_000;
                          if (hoursWaiting < 24) return null;
                          const label = hoursWaiting >= 48 ? `⏰ ${Math.round(hoursWaiting / 24)}י` : `⏰ ${Math.round(hoursWaiting)}ש`;
                          return (
                            <span
                              className="text-[9px] font-semibold flex-shrink-0 leading-none bg-amber-50 text-amber-600 border border-amber-300 rounded px-1 py-0.5"
                              title={`ניתוח AI ממתין לסקירה כבר ${Math.round(hoursWaiting)} שעות — לא טופל`}
                            >{label}</span>
                          );
                        })()}
                        {(() => { const si = _staleInfo(t); return si ? (
                          <span
                            className={`text-[10px] leading-none font-semibold flex-shrink-0 rounded px-1 py-0.5 ${si.cls}`}
                            title={t.status === "waiting" ? "ממתין לתגובת לקוח" : "לא עודכנה מעל 7 ימים — ייתכן שדורשת תשומת לב"}
                          >{si.label}</span>
                        ) : null; })()}
                        {t.ai_category && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setFilter("ai_category", t.ai_category); }}
                            title={`AI סיווג: ${ticketCategoryLabel(t.ai_category)} — לחץ לסינון לפי קטגוריה`}
                            className={`text-[9px] leading-none font-semibold flex-shrink-0 border rounded px-1 py-0.5 hover:opacity-80 ${AI_CATEGORY_STYLE[t.ai_category] || "bg-gray-50 text-gray-500 border-gray-200"}`}
                          >
                            {ticketCategoryLabel(t.ai_category)}
                          </button>
                        )}
                        {t.ai_summary && (
                          <span
                            title={`🧠 AI: ${t.ai_summary}`}
                            className="text-violet-400 text-[10px] leading-none flex-shrink-0 cursor-help select-none"
                            aria-label="AI analysis summary available"
                          >◉</span>
                        )}
                      </div>
                    </td>
                    {/* These two cells are hidden in the narrow desktop table but keep their
                        label for the <640px card layout, where they are shown in full. */}
                    <td className="hidden 2xl:table-cell px-2 xl:px-3 py-2.5 xl:py-3" style={{ overflow: 'hidden' }} data-label="שולח">
                      <div className="font-medium text-gray-700 text-xs leading-tight truncate">{t.sender_name}</div>
                      <div className="text-gray-400 text-xs truncate">{t.sender_email}</div>
                    </td>
                    <td className="px-2 xl:px-3 py-2.5 xl:py-3" style={{ overflow: 'visible', position: 'relative' }} data-label="חברה">
                      <CompanySelect
                        companies={companies}
                        value={t.company ?? null}
                        floating
                        onChange={(company) => {
                          if (updatingCompanyTicketId === t.id) return;
                          updateTicketCompany.mutate({ ticketId: t.id, companyId: company?.id ?? null, company });
                        }}
                        disabled={updatingCompanyTicketId === t.id}
                        inputClassName="w-full border border-gray-200 rounded px-1.5 py-0.5 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand-400 disabled:opacity-50 disabled:cursor-wait"
                      />
                    </td>
                    <td className="px-2 xl:px-3 py-2.5 xl:py-3" style={{ overflow: 'hidden' }} data-label="טכנאי">
                      <select
                        value={t.technician_id ?? ""}
                        aria-label={`טכנאי עבור קריאה #${t.ticket_number}`}
                        onChange={(e) => inlineUpdate.mutate({ id: t.id, data: { technician_id: e.target.value === "" ? null : e.target.value } })}
                        className="border rounded px-1.5 py-0.5 text-xs text-gray-700 bg-white w-full"
                      >
                        <option value="">לא משובץ</option>
                        {t.technician_id && !technicians.some((tech) => tech.id === t.technician_id) && (
                          <option value={t.technician_id}>
                            {t.technician ? `${t.technician.first_name} ${t.technician.last_name}` : t.technician_id}
                          </option>
                        )}
                        {technicians.map((tech) => (
                          <option key={tech.id} value={tech.id}>{tech.first_name} {tech.last_name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 xl:px-3 py-2.5 xl:py-3" style={{ overflow: 'hidden' }} data-testid={`ticket-digital-employee-${t.id}`} data-label="עובד דיגיטלי">
                      {t.digital_employee ? (
                        <div className="flex items-center gap-1.5 min-w-0">
                          {/* isEnabled is passed without a status: the avatar owns the "a disabled employee is offline" rule
                              (drawnStatus), and passing no status keeps an enabled employee from wearing a "זמין" dot this
                              payload never carried. */}
                          <EmployeeAvatar
                            name={t.digital_employee.name}
                            avatarKey={t.digital_employee.avatar_key}
                            avatarColor={t.digital_employee.avatar_color}
                            isEnabled={t.digital_employee.is_enabled}
                            size="sm"
                          />
                          <Link
                            to={`/agents/${t.digital_employee.agent_id}`}
                            className="text-gray-800 hover:text-brand-600 hover:underline truncate"
                            title={t.digital_employee.name}
                          >
                            {t.digital_employee.name}
                          </Link>
                          {t.digital_employee.is_enabled === false && (
                            <span
                              title={`${t.digital_employee.name} מושבת — לא יתחיל לעבוד על הקריאה`}
                              className="text-[10px] font-medium px-1 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200 flex-shrink-0"
                            >
                              מושבת
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-2 xl:px-3 py-2.5 xl:py-3" style={{ overflow: 'hidden' }} data-label="סטטוס">
                      <select
                        value={t.status}
                        aria-label={`סטטוס עבור קריאה #${t.ticket_number}`}
                        onChange={(e) => inlineUpdate.mutate({ id: t.id, data: { status: e.target.value } })}
                        className={`border rounded px-1.5 py-0.5 text-xs font-semibold w-full ${statusColor(t.status)}`}
                      >
                        {ticketStatusSelectOptions(t.status).map((opt) => (
                          <option key={opt.value} value={opt.value} disabled={opt.disabled}>{opt.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 xl:px-3 py-2.5 xl:py-3" style={{ overflow: 'hidden' }} data-label="SLA">
                      {t.is_sla_breach ? (
                        <span className="px-1.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-200">
                          ⚠
                        </span>
                      ) : (
                        /* Card mode prints the label for every cell; an empty value would read as
                           missing data. "—" states the measured fact: this ticket is within SLA. */
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-2 xl:px-3 py-2.5 xl:py-3 text-xs" data-label="מועד יעד">
                      <input aria-label={`מועד יעד לקריאה ${t.ticket_number}`} type="date" value={t.due_date?.slice(0, 10) || ""} onChange={(e) => inlineUpdate.mutate({ id: t.id, data: { due_date: e.target.value || null } })} className={`w-full rounded border px-1 py-0.5 ${isTicketOverdue(t) ? "border-red-400 text-red-700 bg-red-50" : "border-gray-200"}`} />
                      {isTicketOverdue(t) && <span role="img" aria-label="איחור בביצוע" title="המועד עבר והקריאה טרם נסגרה">😞</span>}
                    </td>
                    <td className="px-2 xl:px-3 py-2.5 xl:py-3 text-gray-400 text-xs whitespace-nowrap" style={{ overflow: 'hidden' }} data-label="תאריך פתיחה">
                      {fmtDateTime(t.opened_at)}
                    </td>
                    <td className="hidden 2xl:table-cell px-2 xl:px-3 py-2.5 xl:py-3 text-gray-400 text-xs whitespace-nowrap" style={{ overflow: 'hidden' }} data-label="עדכון אחרון">
                      {fmtDateTime(t.updated_at)}
                    </td>
                    <td className="px-2 xl:px-3 py-2.5 xl:py-3" data-label="פעולות">
                      <button
                        type="button"
                        onClick={() => confirmTicketDeletion(t, window.confirm, deleteMutation.mutate)}
                        disabled={deleteMutation.isPending && deleteMutation.variables === t.id}
                        aria-label={`מחק קריאה #${t.ticket_number} — ${t.title}`}
                        className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} rounded text-gray-300 hover:text-red-500 transition-colors text-base leading-none disabled:opacity-40`}
                        title="מחק קריאה"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50 text-sm">
                <span className="text-xs text-gray-500">
                  עמוד {page + 1} מתוך {totalPages}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setPage((p) => p - 1)}
                    disabled={page === 0}
                    aria-label="העמוד הקודם"
                    className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} px-2.5 py-1.5 rounded-lg border text-xs font-medium disabled:opacity-40 hover:bg-white transition-colors`}
                  >
                    ›
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i)
                    .filter((i) => i === 0 || i === totalPages - 1 || Math.abs(i - page) <= 2)
                    .reduce((acc, i) => {
                      if (acc.length > 0 && i - acc[acc.length - 1].val > 1)
                        acc.push({ val: -1, key: `e${i}` });
                      acc.push({ val: i, key: String(i) });
                      return acc;
                    }, [])
                    .map((item) =>
                      item.val === -1 ? (
                        <span key={item.key} className="px-1 text-gray-400 text-xs select-none">…</span>
                      ) : (
                        <button
                          key={item.key}
                          type="button"
                          onClick={() => setPage(item.val)}
                          aria-label={`עמוד ${item.val + 1}`}
                          aria-current={item.val === page ? "page" : undefined}
                          className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} w-8 h-8 rounded-lg text-xs font-medium transition-colors ${
                            item.val === page
                              ? "bg-brand-600 text-white"
                              : "hover:bg-gray-200 text-gray-600"
                          }`}
                        >
                          {item.val + 1}
                        </button>
                      )
                    )}

                  <button
                    type="button"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page === totalPages - 1}
                    aria-label="העמוד הבא"
                    className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} px-2.5 py-1.5 rounded-lg border text-xs font-medium disabled:opacity-40 hover:bg-white transition-colors`}
                  >
                    ‹
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

    </div>

    {/* Bulk delete confirmation dialog */}
    {showBulkDeleteConfirm && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div ref={bulkDeleteDialogRef} role="dialog" aria-modal="true" aria-labelledby="bulk-delete-title" className="bg-white rounded-xl shadow-xl border p-6 w-80 space-y-4" dir="rtl">
          <h3 id="bulk-delete-title" className="text-base font-semibold text-gray-800">אישור מחיקה</h3>
          <p className="text-sm text-gray-600">
            האם למחוק {selectedTickets.size} קריאות? פעולה זו אינה הפיכה.
          </p>
          {bulkDeleteMutation.isError && (
            <p className="text-xs text-red-500">{bulkDeleteMutation.error?.response?.data?.detail || "שגיאה במחיקה"}</p>
          )}
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowBulkDeleteConfirm(false)}
              className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50"
            >
              ביטול
            </button>
            <button
              onClick={() => bulkDeleteMutation.mutate([...selectedTickets])}
              disabled={bulkDeleteMutation.isPending}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {bulkDeleteMutation.isPending ? "מוחק..." : "מחק"}
            </button>
          </div>
        </div>
      </div>
    )}

    {showBulkCloseConfirm && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div ref={bulkCloseDialogRef} role="dialog" aria-modal="true" aria-labelledby="bulk-close-title" className="bg-white rounded-xl shadow-xl border p-6 w-80 space-y-4" dir="rtl">
          <h3 id="bulk-close-title" className="text-base font-semibold text-gray-800">אישור סגירה</h3>
          <p className="text-sm text-gray-600">
            האם לסגור את כל הקריאות המסומנות?
          </p>
          {bulkCloseMutation.isError && (
            <p className="text-xs text-red-500">{bulkCloseMutation.error?.response?.data?.detail || "שגיאה בסגירת הקריאות"}</p>
          )}
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowBulkCloseConfirm(false)}
              className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50"
            >
              ביטול
            </button>
            <button
              onClick={() => bulkCloseMutation.mutate([...selectedTickets])}
              disabled={bulkCloseMutation.isPending}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {bulkCloseMutation.isPending ? "סוגר..." : "סגור"}
            </button>
          </div>
        </div>
      </div>
    )}

    {showBulkAssignDialog && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div ref={bulkAssignDialogRef} role="dialog" aria-modal="true" aria-labelledby="bulk-assign-title" className="bg-white rounded-xl shadow-xl border p-6 w-80 space-y-4" dir="rtl">
          <h3 id="bulk-assign-title" className="text-base font-semibold text-gray-800">הקצה לנציג</h3>
          <p className="text-sm text-gray-600">
            בחר טכנאי להקצאת {selectedTickets.size} הקריאות המסומנות.
          </p>
          {techniciansFailed && technicians.length === 0 ? (
            <div role="alert" className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg space-y-1">
              <p>לא ניתן לטעון את רשימת הטכנאים</p>
              <button onClick={() => refetchTechnicians()} className="text-brand-600 hover:underline">נסה שוב</button>
            </div>
          ) : (
          <select
            value={bulkAssignTechnicianId}
            onChange={(e) => setBulkAssignTechnicianId(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">בחר טכנאי</option>
            {technicians.map((tech) => (
              <option key={tech.id} value={tech.id}>{tech.first_name} {tech.last_name}</option>
            ))}
          </select>
          )}
          {bulkAssignMutation.isError && (
            <p className="text-xs text-red-500">{bulkAssignMutation.error?.response?.data?.detail || "שגיאה בהקצאה"}</p>
          )}
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => {
                setShowBulkAssignDialog(false);
                setBulkAssignTechnicianId("");
              }}
              className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50"
            >
              ביטול
            </button>
            <button
              onClick={() => bulkAssignMutation.mutate({ ids: [...selectedTickets], technicianId: bulkAssignTechnicianId })}
              disabled={!bulkAssignTechnicianId || bulkAssignMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {bulkAssignMutation.isPending ? "מקצה..." : "הקצה"}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Split dialog */}
    {showSplitDialog && (() => {
      const sourceTicket = sortedTickets.find((t) => selectedTickets.has(t.id));
      if (!sourceTicket) return null;
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div ref={splitDialogRef} role="dialog" aria-modal="true" aria-labelledby="split-dialog-title" className="bg-white rounded-xl shadow-xl border p-6 w-[30rem] space-y-4" dir="rtl">
            <h3 id="split-dialog-title" className="text-base font-semibold text-gray-800">
              פיצול קריאה #{sourceTicket.ticket_number}
            </h3>
            <div>
              <label className="block text-xs text-gray-500 mb-1">נושא הקריאה החדשה</label>
              <input
                aria-label="נושא הקריאה החדשה"
                value={splitForm.new_subject}
                onChange={(e) => setSplitForm((f) => ({ ...f, new_subject: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">תוכן / הערה לקריאה החדשה</label>
              <textarea
                aria-label="תוכן / הערה לקריאה החדשה"
                rows={4}
                value={splitForm.note}
                onChange={(e) => setSplitForm((f) => ({ ...f, note: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">שבץ לטכנאי</label>
              {techniciansFailed && technicians.length === 0 ? (
                <div role="alert" className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg space-y-1">
                  <p>לא ניתן לטעון את רשימת הטכנאים</p>
                  <button onClick={() => refetchTechnicians()} className="text-brand-600 hover:underline">נסה שוב</button>
                </div>
              ) : (
              <select
                aria-label="שבץ לטכנאי"
                value={splitForm.technician_id}
                onChange={(e) => setSplitForm((f) => ({ ...f, technician_id: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">ללא שיבוץ</option>
                {technicians.map((tech) => (
                  <option key={tech.id} value={tech.id}>{tech.first_name} {tech.last_name}</option>
                ))}
              </select>
              )}
            </div>
            {splitMutation.isError && (
              <p className="text-xs text-red-500">{splitMutation.error?.response?.data?.detail || "שגיאה בפיצול"}</p>
            )}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowSplitDialog(false)}
                className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50"
              >
                ביטול
              </button>
              <button
                onClick={() => splitMutation.mutate({
                  source_ticket_id: sourceTicket.id,
                  new_subject: splitForm.new_subject,
                  note: splitForm.note,
                  technician_id: splitForm.technician_id || null,
                })}
                disabled={!splitForm.new_subject.trim() || splitMutation.isPending}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {splitMutation.isPending ? "מפצל..." : "פצל קריאה"}
              </button>
            </div>
          </div>
        </div>
      );
    })()}

    {/* Merge dialog */}
    {showMergeDialog && (() => {
      const selectedList = sortedTickets.filter((t) => selectedTickets.has(t.id));
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div ref={mergeDialogRef} role="dialog" aria-modal="true" aria-labelledby="merge-dialog-title" className="bg-white rounded-xl shadow-xl border p-6 w-96 space-y-4" dir="rtl">
            <h3 id="merge-dialog-title" className="text-base font-semibold text-gray-800">איחוד קריאות</h3>
            <p className="text-sm text-gray-600">בחר את הקריאה הראשית שתקלוט את שאר הקריאות:</p>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {selectedList.map((t) => (
                <button
                  key={t.id}
                  onClick={() =>
                    mergeMutation.mutate({
                      primaryId: t.id,
                      secondaryIds: selectedList.filter((s) => s.id !== t.id).map((s) => s.id),
                    })
                  }
                  disabled={mergeMutation.isPending}
                  className="w-full text-right px-3 py-2.5 rounded-lg border hover:border-teal-400 hover:bg-teal-50 transition-colors disabled:opacity-50"
                >
                  <div className="text-sm font-medium text-gray-800 truncate">
                    #{t.ticket_number} — {t.title}
                  </div>
                  <div className="text-xs text-gray-400">{t.sender_name}</div>
                </button>
              ))}
            </div>
            {mergeMutation.isError && (
              <p className="text-xs text-red-500">{mergeMutation.error?.response?.data?.detail || "שגיאה באיחוד"}</p>
            )}
            <div className="flex justify-end">
              <button
                onClick={() => setShowMergeDialog(false)}
                className="border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50"
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      );
    })()}
    </>
  );
}
